// SidebarBucketHeader.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './SidebarBucketHeader.css';

// Icons
import Icon from 'svg/Icon';

/**
 * SidebarBucketHeader
 *
 * Renders the expandable header row for a sidebar bucket.
 *
 * @param {string} bucketKey - Unique key for the bucket.
 * @param {string} title - Display title for the bucket.
 * @param {boolean} isOpen - Whether the bucket is currently expanded.
 * @param {function} onToggle - Called when the header is clicked.
 *
 * @returns {ReactNode} The rendered sidebar bucket header.
 */
const SidebarBucketHeader = ({
    bucketKey,
    title,
    isOpen,
    onToggle,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [isHovered, setIsHovered] = useState(false);

    // ----- HANDLERS ----- //

    /**
     * handleClick
     *
     * Toggles the expanded state for the current bucket.
     *
     * @returns {void}
     */
    /**
 * Handles click.
 */

    const handleClick = () => {
        onToggle?.(bucketKey);
    };

    /**
     * handleMouseEnter
     *
     * Enables the hovered header styling.
     *
     * @returns {void}
     */
    /**
 * Handles mouse enter.
 */
    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    /**
     * handleMouseLeave
     *
     * Clears the hovered header styling.
     *
     * @returns {void}
     */
    /**
 * Handles mouse leave.
 */
    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // ----- RENDER ----- //

    return (
        <div
          className="sidebar-bucket-header"
          style={{
                backgroundColor: isHovered ? theme.backgroundHover : 'transparent',
            }}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
            <div
                style={{
                    color: isHovered ? theme.foreground : theme.foregroundMuted,
                }}
            >
                {title}
            </div>

            {/* HEADER ICON */}
            <div className="sidebar-bucket-arrow">
                <Icon
                  iconKey={isOpen ? 'indeterminate_check_box' : 'add_box'}
                  color={isHovered ? theme.foreground : theme.foregroundMuted}
                  width={20}
                  height={20}
                />
            </div>
        </div>
    );
};

// Dev Tools
SidebarBucketHeader.displayName = 'SidebarBucketHeader';

export default SidebarBucketHeader;
